import { MessageQueue } from "../MessageQueue.js";
import { clearObj, isConstructor, isFunction, removeFromArray } from "../utils.js";
import { System, SystemDependency } from "./System.js";
import { EntityManager } from "../EntityManager.js";
import { Component } from "../Component.js";
import { Constructor } from "../types.js";
import { type IDebugSystem, NOOP_DEBUG_LOGGER, DEBUG_SYSTEM_NAME } from "../debug/types.js";

/**
 * System constructor type used by `Process` helpers.
 */
export interface ISystem {
    new (...args: Array<any>): System
}

/**
 * Owner key recorded for systems that no area file added — the game file's own
 * systems plus anything added directly through `addSystem`. Area files pass
 * their area TYPE as the owner instead (see `systemOwners`).
 */
export const GAME_FILE_SYSTEM_OWNER = '__game__';

/**
 * Is this function a `class` in source form?
 *
 * Only a backstop for the ANONYMOUS class `isConstructor` can not see (it reads
 * `prototype.constructor.name`, which is empty there). A downlevelled class is
 * a plain `function` and stays `isConstructor`'s job — this never has to catch
 * it. Used to keep a class from being mistaken for a system factory.
 */
function isClassSource(f: any): boolean {
  return typeof f === 'function' && /^\s*class[\s{]/.test(Function.prototype.toString.call(f));
}

/** One game/area-file `systems` entry, resolved to the system it adds. */
export type ResolvedSystemEntry = {
  /** The system's name — its key in `systemLookup`. */
  name: string | number;
  /**
   * What to hand `addSystem` / `restartOrStartSystem`: the class when its system
   * name was already known, otherwise the instance that had to be built to learn
   * the name — so a system is never constructed twice.
   */
  entry: ISystem | System;
  /** The system's class. */
  ctor: Function;
};

/**
 * Resolve one `systems` entry exactly as `addSystemsArrayFromFile` reads it: a
 * factory (`() => Class` / `() => instance`) is called; a class whose name a
 * process has already mapped (`constructorNamesToSystemNames`, filled by
 * `addSystem`) is named without being constructed; any other class is
 * constructed ONCE and that instance is returned as the entry to add; an
 * instance names itself.
 *
 * For tools that must know an entry's system name before applying it (hot
 * reload, diffing a new area file against the running one).
 */
export function resolveSystemEntry(
  raw: unknown,
  known: { constructorNamesToSystemNames: { [ctorName: string]: string | number } } = { constructorNamesToSystemNames: {} },
): ResolvedSystemEntry {
  let s: any = raw;
  if (isFunction(s) && !isConstructor(s) && !isClassSource(s)) s = s();
  if (typeof s === 'function') {
    const mapped = s.name ? known.constructorNamesToSystemNames[s.name] : undefined;
    if (mapped !== undefined) return { name: mapped, entry: s as ISystem, ctor: s };
    const instance: System = new (s as ISystem)();
    return { name: instance.name, entry: instance, ctor: s };
  }
  if (s && typeof s === 'object' && 'name' in s) return { name: s.name, entry: s as System, ctor: s.constructor };
  throw new Error(`A systems entry is not a system class, instance or factory: ${String(raw)}`);
}

/** One `Process.update` call, timed. */
export type TickSample = {
  /** The `delta` the tick ran with. */
  delta: number;
  /** Wall time the whole update took (before/after callbacks, every system, local messages). */
  durationMs: number;
  /** `Date.now()` when the tick finished. */
  at: number;
};

/** One system's `onUpdate`, timed. */
export type SystemUpdateSample = {
  system: string | number;
  delta: number;
  durationMs: number;
  at: number;
};

/**
 * What `Process.observe` reports. Every member is optional; an observer pays
 * only for what it implements (with no tick/system-update observer the loop
 * runs exactly as it does unobserved).
 */
export type ProcessObserver = {
  /** After every `update(delta)` — also when a system threw out of it. */
  onTick?(sample: TickSample): void;
  /** After each system's `onUpdate` — also when it threw. */
  onSystemUpdate?(sample: SystemUpdateSample): void;
  /** A system was added (`addSystem`, a game/area file, `restartOrStartSystem` starting one). */
  onSystemAdded?(system: System): void;
  /** A system left for good (`removeSystem` dropped its last owner). */
  onSystemRemoved?(system: System): void;
  /** `restartOrStartSystem` replaced `previous` with `next` (not fired for a rolled-back attempt). */
  onSystemRestarted?(next: System, previous: System): void;
};

/**
 * Base process that owns systems, entities, and update loops.
 */
export abstract class Process { 
  readonly $api: Record<string, any> = {};
  readonly $gameData : any = {};
  readonly messageQueue : MessageQueue = new MessageQueue()
  readonly systemLookup : {[system: string | number]: System } = {};
  readonly systemArray : Array<System> = [];
  readonly systemUpdates : Array<(delta: number) => void> = [];
  /** Which system each bound `onUpdate` in `systemUpdates` belongs to (for `onSystemUpdate` samples). */
  private readonly _updateOwners = new WeakMap<Function, System>();
  private _observers: ProcessObserver[] = [];
  private _tickObservers: ProcessObserver[] = [];
  private _systemUpdateObservers: ProcessObserver[] = [];
  public afterUpdateHandler?: (delta: number) => void
  public beforeUpdateHandler?: (delta: number) => void

  // Internal next-tick scheduling managed centrally by the process
  // Current-tick queues (promoted from previous frame at start of update)
  public _beforeTickCbs: Array<(delta: number) => void> = [];
  public _afterTickCbs: Array<(delta: number) => void> = [];
  // Next-tick queues (accumulate during the frame; promoted next update)
  public _beforeNextTickCbs: Array<(delta: number) => void> = [];
  public _afterNextTickCbs: Array<(delta: number) => void> = [];

  readonly constructorNamesToSystemNames : {[key: string]: string | number } = {};
  readonly systemStartCounts : {[systemName: string | number]: number} = {}

  /**
   * Which file added each system: `systemName -> Set<ownerKey>`, alongside the
   * ref count in `systemStartCounts`. The owner key is opaque here — the client
   * passes an area TYPE, and everything else uses `GAME_FILE_SYSTEM_OWNER` —
   * so a client can resolve "which area does this system belong to?" when a
   * system dispatches to the server without naming an area
   * (`ClientProcess.resolveAreaForSystem`).
   *
   * Ref count and owner set are deliberately NOT the same number: an area type
   * that starts twice ref-counts twice but contributes one owner.
   */
  readonly systemOwners : {[systemName: string | number]: Set<string>} = {}

  /** Owner key for the `addSystemsArrayFromFile` call currently in progress. */
  private _addingSystemOwner?: string;
  readonly systemsWithOnLocalMessages: Array<System> = [];

  readonly apiSystemLookup: {[name: string]: Array<string>} = {};
  // Caller-aware API factories keyed by API name
  readonly callerApiFactories: { [name: string]: { system: System, factory: (caller: System) => { handler: Function, cleanup?: Function } } } = {};
  // Track per-caller cleanups per API name
  readonly _callerApiCleanups: WeakMap<System, Map<string, Set<(...args: any[]) => void>>> = new WeakMap();
  readonly _callersPatchedForCleanup: WeakSet<System> = new WeakSet();

  readonly entityManager : EntityManager;
  public started: boolean = false;
  public startedLoop : boolean = false;

  /** 
   * Debug system reference (set when a debug system is added).
   * Used to create $debugLogger instances for systems and entities.
   */
  protected _debugSystem?: IDebugSystem;

  constructor() {
    this.systemLookup = {};
    this.systemArray = [];
    this.entityManager = new EntityManager(this.systemLookup, this.$api, this.$gameData);
  }

  /**
   * Wraps entityManager methods to inject debug loggers into entities.
   * Called once when the debug system is added. All debug injection logic
   * is centralized here in Process rather than scattered across EntityManager.
   */
  private _wrapEntityManagerForDebug(): void {
    if (!this._debugSystem) return;
    
    const debugSystem = this._debugSystem;
    const originalInit = this.entityManager.initializeEntity.bind(this.entityManager);
    const originalDestroy = this.entityManager.destroyEntity.bind(this.entityManager);
    
    // Wrap initializeEntity to inject $debugLogger
    this.entityManager.initializeEntity = <InitType>(entity: any, data?: InitType) => {
      const result = originalInit(entity, data);
      const entityType = entity.constructor?.name || 'Entity';
      entity.$debugLogger = debugSystem.createEntityLogger(entity.id, entityType);
      return result;
    };
    
    // Wrap destroyEntity to clean up debug info
    this.entityManager.destroyEntity = (entity: any, options?: any) => {
      if ('removeEntityDebugInfo' in debugSystem) {
        (debugSystem as any).removeEntityDebugInfo(entity.id);
      }
      return originalDestroy(entity, options);
    };
  }

  /**
   * Enable the debug system. Call this from subclasses (ServerProcess/ClientProcess)
   * when the game file has `debug: true`. The subclass should pass its own DebugSystem
   * constructor (ServerDebugSystem or ClientDebugSystem).
   * @param DebugSystemClass - The debug system class to instantiate and add
   * @param config - Optional configuration to pass to the debug system constructor
   */
  protected enableDebugSystem<D extends System>(DebugSystemClass: new (config?: any) => D, config?: any): D {
    if (this._debugSystem) {
      // Already enabled, return existing
      return this._debugSystem as unknown as D;
    }
    const debugSystem = new DebugSystemClass(config);
    return this.addSystem(debugSystem);
  }

  private remakeSystemHookImplementationArrays() {
    this.systemUpdates.length = 0;
    this.systemsWithOnLocalMessages.length = 0;
    this.systemArray.forEach(sys => {
      if(sys.onUpdate) {
        this.systemUpdates.push(sys.onUpdate);
      }
      if(sys.onLocalSystemMessage) {
        this.systemsWithOnLocalMessages.push(sys)
      }
    })
  }
  public abstract decorate(system : System< any>) : System< any>
  public abstract onAddedSystem(system: System)
  protected abstract onStartLoop(...opts: any[]) : void;
  protected abstract onStopLoop(...opts: any[]) : void

  public setGameData(data: any = {}) {
    clearObj(this.$gameData);
    for(let key in data) {
      this.$gameData[key] = data[key];
    }
  }
  public updateGameData(data: any = {}) {
    for(let key in data) {
      this.$gameData[key] = data[key];
    }
  }

  public hasSystemDependency(dependency: SystemDependency) {
    if(typeof dependency === 'string' || typeof dependency === 'number') {
      return dependency in this.systemLookup;
    } else if (typeof dependency === 'object' && dependency.name) {
      return dependency.name in this.systemLookup &&
          (!dependency.version || this.systemLookup[dependency.name].$version === dependency.version);
    }
    return false;
  }

  public getMissingSystemDependencies(dependencies: SystemDependency[]) : SystemDependency[] {
    if(!dependencies || !dependencies.length) return [];
    return dependencies.filter(dep => {
      return !this.hasSystemDependency(dep);
    });
  }

  public hasApiDependency(dependency: string, type: 'any' | 'function' | 'object' | 'string' | 'number'='any') {
    if(type === 'any') {
        return (dependency in this.$api) || (dependency in this.callerApiFactories);
    } else {
      if(dependency in this.$api) {
        return typeof this.$api[dependency] === type;
      }
      if(dependency in this.callerApiFactories) {
        // Caller APIs are always functions from the consumer perspective
        return type === 'function';
      }
      return false;
    }
  }
  //todo can eventually use func.length and make api dep an optional object that checks number of params the func takes
  public getMissingApiDependencies(dependencies: string[]) : string[] {
    if(!dependencies || !dependencies.length) return [];
    return dependencies.filter(dep => {
      return !this.hasApiDependency(dep);
    });
  }
  // todo: something for adding event apis without needing to define all the listener bs, also to unlisten automatically
  // when a system listens to the event and is stopped, or if an entity listens to it and is dfestroyed, or a component is removed
  //public addApiEvent<K extends string>(name: K, value: any, system?: System | string | number) {}
  
  public addApi<K extends string>(
    name: K,
    value: any,
    system?: System | string | number
  ) : void;
  public addApi<K extends string>(
    config: {
      name: K,
      system?: (caller: System) => { handler: any & Function, cleanup?: Function } | (any & Function),
      handler?: any
    },
    systemOrName?: System | string | number
  ) : void;
  public addApi<K extends string>(
    nameOrConfig: any,
    valueOrSystem?: any,
    maybeSystem?: any
  ) {
    // Object overload supporting system-scoped and/or global handler
    if (typeof nameOrConfig === 'object' && nameOrConfig && 'name' in nameOrConfig) {
      const cfg = nameOrConfig as { name: K, system?: (caller: System) => { handler: any & Function, cleanup?: Function } | (any & Function), handler?: any };
      const name = cfg.name as unknown as K;
      const systemName: string | number = typeof valueOrSystem === 'object' && valueOrSystem ? (valueOrSystem as System).name as any : (valueOrSystem as any);
      const hadSystemName = systemName || systemName === 0 || systemName === '0';
      if (!name) {
        if (hadSystemName) {
          throw new Error(`no name provided for api method ${String(name)} from system ${systemName}`);
        } else {
          throw new Error(`no name provided for api method ${String(name)}`);
        }
      }
      if ((name as any) in this.$api) {
        throw new Error(`Trying to add the value ${String(name)} to the api twice.`);
      }
      const providerSystem: System | undefined = typeof valueOrSystem === 'object' ? valueOrSystem as System : (hadSystemName ? this.systemLookup[systemName] : undefined);
      if ((cfg.system || typeof cfg.handler === 'function') && hadSystemName && !providerSystem) {
        throw new Error(`No system object to bind the value for added api: ${String(name)} in system: ${systemName}`);
      }
      if (hadSystemName) {
        if (!this.apiSystemLookup[systemName]) {
          this.apiSystemLookup[systemName] = [name as unknown as string];
        } else {
          this.apiSystemLookup[systemName].push(name as unknown as string);
        }
      }

      // If a system-scoped factory is provided, register it
      if (cfg.system) {
        const rawFactory = cfg.system;
        const boundFactory = (providerSystem ? (rawFactory as Function).bind(providerSystem) : rawFactory) as (caller: System) => any;
        const normalizedFactory = (caller: System) => {
          const out = boundFactory(caller);
          if (typeof out === 'function') {
            return { handler: out };
          }
          return out as { handler: Function, cleanup?: Function };
        };
        this.callerApiFactories[String(name)] = { system: providerSystem!, factory: normalizedFactory };
      }

      // Determine global/process-level handler
      let globalValue: any | undefined = cfg.handler;
      if (typeof globalValue === 'function') {
        if (providerSystem) {
          globalValue = (globalValue as unknown as Function).bind(providerSystem) as any as any;
        }
      }
      if (cfg.system && !cfg.handler) {
        // Expose throwing placeholder at process/global level
        (this.$api as any)[name] = ((..._args: unknown[]) => { throw new Error(`The api method: ${String(name)} was added with system-scoped behavior, meaning it can only be used inside of a system context.`); }) as any;
      } else {
        // Set the global value (can be function or non-function)
        (this.$api as any)[name] = globalValue as any;
      }

      // Apply to each existing system snapshot
      this.systemArray.forEach(sys => {
        if (cfg.system) {
          // caller-aware wrapper for systems
          const { factory } = this.callerApiFactories[String(name)];
          const produced = factory(sys);
          const handler = produced?.handler;
          const cleanup = produced?.cleanup;
          if (typeof handler !== 'function') {
            (sys.$api as any)[name] = (() => { throw new Error(`Caller API ${String(name)} handler is not a function`); }) as any;
            return;
          }
          (sys.$api as any)[name] = ((...args: any[]) => {
            if (cleanup) {
              let perCaller = this._callerApiCleanups.get(sys);
              if (!perCaller) { perCaller = new Map(); this._callerApiCleanups.set(sys, perCaller); }
              let perApi = perCaller.get(String(name));
              if (!perApi) { perApi = new Set(); perCaller.set(String(name), perApi); }
              const cleanupFn = () => (cleanup as any)(...args);
              perApi.add(cleanupFn);
              if (!this._callersPatchedForCleanup.has(sys)) {
                const originalOnStop = sys.onStop?.bind(sys);
                sys.onStop = (...stopArgs: any[]) => {
                  const map = this._callerApiCleanups.get(sys);
                  if (map) {
                    map.forEach((set) => { set.forEach(fn => { try { fn(); } catch {} }); });
                    this._callerApiCleanups.delete(sys);
                  }
                  originalOnStop?.(...stopArgs);
                };
                this._callersPatchedForCleanup.add(sys);
              }
            }
            return (handler as any)(sys, ...args);
          }) as any;
        } else {
          // regular/global value
          (sys.$api as any)[name] = globalValue as any;
        }
      });
      return;
    }

    // Legacy overload (name, value, [system])
    const name = nameOrConfig as K;
    const value = valueOrSystem as any;
    const system = maybeSystem as (System | string | number | undefined);
    const systemName : string | number  = typeof system === 'object' && system ? (system as System).name as string | number : system as string | number;
    const hadSystemName = systemName || systemName === 0 || systemName === '0';
    if(!name) {
      if(hadSystemName) {
        throw new Error(`no name provided for api method ${name as string} from system ${systemName} if youre adding an anonynmous function to the api from a system you must supply a name as second parameter`);
      } else {
        throw new Error(`no name provided for api method ${name as string}`);
      }
    }
    const systemObject : System = typeof system === 'object' ? system as System : this.systemLookup[systemName];

    if(name in this.$api) {
      throw new Error(`Trying to add the value ${name as string} to the api twice.`);
    }
    if(hadSystemName) {
      if(!this.apiSystemLookup[systemName]) {
        this.apiSystemLookup[systemName] = [name as string];
      } else {
        this.apiSystemLookup[systemName].push(name as string);
      }
    }
    let finalValue = value;
    if (typeof finalValue === "function") {
      if(!systemObject && hadSystemName) {
        throw new Error(`No system object to bind the value for added api: ${name as string} in system: ${systemName}`)
      }
      if(systemObject) {
        finalValue = (finalValue as unknown as Function).bind(systemObject) as any as any;
      }
    }
    (this.$api as any)[name] = finalValue as any;
    this.systemArray.forEach(sys => {
      (sys.$api as any)[name] = finalValue as any;
    });
  }

  // Removed: addSystemScopedApi in favor of addApi object overload

  // Build a snapshot of the shared $api for a specific system (no Proxy)
  public buildApiForSystem(system: System): Record<string, any> {
    const result = { ...this.$api } as Record<string, any>;
    for (const key in this.callerApiFactories) {
  const { factory } = this.callerApiFactories[key as string];
  const { handler, cleanup } = factory(system);
      if (typeof handler !== 'function') {
        (result as Record<string, unknown>)[key as string] = ((..._args: any[]) => { throw new Error(`Caller API ${String(key)} handler is not a function`); }) as any;
        continue;
      }
      (result as Record<string, unknown>)[key as string] = ((...args: any[]) => {
        if (cleanup) {
          let perCaller = this._callerApiCleanups.get(system);
          if (!perCaller) {
            perCaller = new Map();
            this._callerApiCleanups.set(system, perCaller);
          }
          let perApi = perCaller.get(key as string);
          if (!perApi) {
            perApi = new Set();
            perCaller.set(key as string, perApi);
          }
          const cleanupFn = () => (cleanup as any)(...args);
          perApi.add(cleanupFn);
          if (!this._callersPatchedForCleanup.has(system)) {
            const originalOnStop = system.onStop?.bind(system);
            system.onStop = (...stopArgs: any[]) => {
              const map = this._callerApiCleanups.get(system);
              if (map) {
                map.forEach((set) => {
                  set.forEach(fn => { try { fn(); } catch {} });
                });
                this._callerApiCleanups.delete(system);
              }
              originalOnStop?.(...stopArgs);
            };
            this._callersPatchedForCleanup.add(system);
          }
        }
        return (handler as any)(system, ...args);
      }) as any;
    }
  // Inject scheduling helpers so systems can call this.$api.nextTick* as well
  (result as unknown as Record<string, any>)["nextTickBeforeUpdate"] = (cb: (delta: number) => void) => this.nextTickBeforeUpdate(cb);
  (result as unknown as Record<string, any>)["nextTickAfterUpdate"] = (cb: (delta: number) => void) => this.nextTickAfterUpdate(cb);
        return result;
      }

    // todo: something for adding event apis without needing to define all the listener bs, also to unlisten automatically
    // when a system listens to the event and is stopped, or if an entity listens to it and is dfestroyed, or a component is removed
    //public addApiEvent<K extends string>(name: K, value: any, system?: System | string | number) {}
   
  public removeApi<K extends string>(name: K, skipSysLookup = false) {
      if(!(name in this.$api)) {
          throw new Error(`Trying to remove api: ${name as string} but it did not exist.`);
      }
      const key = name as string;
      if(!skipSysLookup) {
          const sysApiKeys = Object.keys(this.apiSystemLookup)
          sysApiKeys.forEach(k => {
              const idx = this.apiSystemLookup[k].indexOf(key);
              if (idx > -1) {
                  this.apiSystemLookup[k].splice(idx, 1);
                  if(!this.apiSystemLookup[k].length) {
                      delete this.apiSystemLookup[k];
                  }
              }
          });
      }
      // Always remove from each system's local $api snapshot in-place (no full rebuild)
      this.systemArray.forEach(s => {
        delete (s.$api as Record<string, any>)[name];
      });
      // Remove from process-level $api
      delete this.$api[name];
      delete this.callerApiFactories[name as string];
      // Do not rebuild all system APIs here
  }

	public removeApis(names: string[]) {
    names.forEach(n => {
      this.removeApi(n, true);
    });
  }

  /**
   * Replace the running system of the same name with `systemOrConstructor`
   * (hot reload), or start it when none is running.
   *
   * Restart order: old `onBeforeRestart` → new `onAfterRestartBeforeInit` →
   * `onInit` → (started) `onAfterRestartBeforeStart` → `onStart` →
   * `onAfterRestart`. The replaced instance is then RELEASED (`releaseSystem`:
   * a subclass drops what it attached to it — asset subscriptions, raw-message
   * slots), so it never receives another callback.
   *
   * Starting a system that was not running records it like `addSystem` does
   * (start count + `owner`, default the game-file owner), so a later
   * `removeSystem` removes it. Replacing keeps the running system's owners.
   *
   * ROLLBACK: if anything after the old instance was taken out throws (the new
   * system's restart hooks, `onInit`, `onStart`, or wiring it), the new instance
   * is removed and released, the old instance is put back at its index with
   * the `$api` entries it had registered, and the error is rethrown — the
   * process is left running exactly the systems it ran before. The old
   * instance's own state is whatever its `onBeforeRestart` left; no lifecycle
   * hook is re-run on it.
   */
  public restartOrStartSystem(systemOrConstructor: ISystem | System, owner?: string): System {
    let system : System = systemOrConstructor as System;
    if(isConstructor(systemOrConstructor)) {
      const systemConstructor : ISystem = systemOrConstructor as ISystem;
      system = new systemConstructor();
      this.constructorNamesToSystemNames[systemOrConstructor.name] = system.name;
    }
    system = this.decorate(system);
    const name = system.name;
    const previousApis = this.snapshotSystemApis(name);
    const { system: oldSystem, index, restartData } = this._removeSystem(name as unknown as string, true);
    try {
      // Attach the new system at the same index for stable ordering
      this._addSystem(system, index);
      if (oldSystem) {
        system.onAfterRestartBeforeInit?.(restartData);
      }
      // Always initialize the new system
      system.__init();
      if (this.started) {
        if (oldSystem) {
          (system as any).onAfterRestartBeforeStart?.(restartData);
        }
        system.onStart?.();
      }
      if (oldSystem) {
        system.onAfterRestart?.(restartData);
      }
    } catch (err) {
      this.rollbackRestart(system, oldSystem, index, previousApis);
      throw err;
    }
    if (oldSystem) {
      this.releaseSystem(oldSystem);
      this._notify('onSystemRestarted', system, oldSystem);
    } else {
      this.systemStartCounts[name] = 1;
      (this.systemOwners[name] ??= new Set<string>()).add(owner ?? this._addingSystemOwner ?? GAME_FILE_SYSTEM_OWNER);
      this._notify('onSystemAdded', system);
    }
    return system;
  }

  /**
   * Called once a system instance has left the process for good — removed
   * (`removeSystem` dropped its last owner), replaced by `restartOrStartSystem`,
   * or a failed replacement being rolled back. Subclasses drop whatever they
   * attached to the instance in `onAddedSystem`, so a released instance never
   * receives another callback. Must be idempotent.
   */
  protected releaseSystem(_system: System): void {}

  /** The `$api` entries a system registered (by `addApi`), to restore on rollback. */
  private snapshotSystemApis(systemName: string | number) {
    const names = [...(this.apiSystemLookup[systemName] ?? [])];
    return names.map(n => ({ name: n, value: this.$api[n], callerFactory: this.callerApiFactories[n] }));
  }

  private rollbackRestart(failed: System, previous: System | undefined, index: number, previousApis: ReturnType<Process['snapshotSystemApis']>) {
    if (this.systemLookup[failed.name] === failed) {
      // Drops the failed instance AND every $api entry registered under the
      // system's name — at this point only the failed instance's.
      this._removeSystem(failed);
    } else {
      // _addSystem threw before the lookup took it: it may still sit in the array.
      removeFromArray(this.systemArray, failed);
    }
    this.releaseSystem(failed);
    if (!previous) return;
    // Re-adding re-runs setProcess (which re-registers the APIs the instance
    // pooled before it had a process) and onAddedSystem; the rest of what it
    // registered is restored from the snapshot.
    this._addSystem(previous, index > -1 ? Math.min(index, this.systemArray.length) : undefined);
    for (const { name, value, callerFactory } of previousApis) {
      if (name in this.$api) continue;
      (this.apiSystemLookup[previous.name] ??= []).push(name);
      this.$api[name] = value;
      if (callerFactory) this.callerApiFactories[name] = callerFactory;
      this.systemArray.forEach(s => {
        (s.$api as Record<string, any>)[name] = callerFactory ? this.buildApiForSystem(s)[name] : value;
      });
    }
  }

  // Overloads to preserve concrete subclass type
  public addSystem<S extends System>(system: S, atIndex?: number): S;
  public addSystem<S extends System>(ctor: { new (...args: any[]): S }, atIndex?: number): S;
  public addSystem(systemOrConstructor: ISystem | ISystem | System | Constructor<System>, atIndex?: number) : System {
    let system : System = systemOrConstructor as System;
    const systemName = this.getSystemName(systemOrConstructor);
    if((systemName || systemName === 0 || systemName === "0") && systemName in this.systemStartCounts) {
      this.systemStartCounts[systemName]++;
      // A SECOND file adding an already-running system: the system itself is
      // untouched but its ownership widens, which is exactly what makes an
      // implicit dispatch target ambiguous — record it here or the early
      // return silently loses it.
      this.recordSystemOwner(systemName);
      return;
    }
    if(isConstructor(systemOrConstructor)) {
      const systemConstructor : ISystem = systemOrConstructor as ISystem;
      system = new systemConstructor();
      this.constructorNamesToSystemNames[systemOrConstructor.name] = system.name;
    }
    system = this.decorate(system)
    this.systemStartCounts[system.name] = 1;
    this.recordSystemOwner(system.name);
    this._addSystem(system, atIndex);
    system.__init();
    this._notify('onSystemAdded', system);
    return system;
  }

  public async addApiFromFile<ApiT, DataT=any, ConfigOptionsT=any>(api: Partial<ApiT> | ((data: DataT, envConfigOptions?: ConfigOptionsT) => Partial<ApiT> | Promise<Partial<ApiT>>), gameData: DataT, envConfigOptions?: ConfigOptionsT) : Promise<string[]> {
      const addedApi : string[] = [];
      api = typeof api === "function" ?  await api(gameData, envConfigOptions) : api;
      for(let key in api) {
          this.addApi(key, api[key] as any);
          addedApi.push(key);
      }
      return addedApi;
  }

  /**
   * Record the owner of the `addSystemsArrayFromFile` call in progress (or the
   * game-file sentinel for a bare `addSystem`) against `systemName`.
   */
  private recordSystemOwner(systemName: string | number) {
    const owner = this._addingSystemOwner ?? GAME_FILE_SYSTEM_OWNER;
    (this.systemOwners[systemName] ??= new Set<string>()).add(owner);
  }

  public addSystemsArrayFromFile(systems: Array<ISystem | System | (() => ISystem) | (() => System)>, owner?: string ) : Array<System> {
    const startedSystems : Array<System> = [];
    const previousOwner = this._addingSystemOwner;
    this._addingSystemOwner = owner ?? GAME_FILE_SYSTEM_OWNER;
    try {
      for(let i = 0; i < systems.length; i++) {
        let s = systems[i];
        // A game/area file may list a FACTORY — `() => SystemClass` or
        // `() => new SystemClass()` — to defer construction until the file is
        // started. Resolve it to whatever it returns, then treat that like any
        // other entry. A class is itself a function, so it must never be
        // mistaken for a factory and called: `isConstructor` catches named
        // classes (and downlevelled ones, which compile to functions), and the
        // source check catches anonymous ones, which have no `.name` for
        // `isConstructor` to see. Calling one of those would throw an opaque
        // "Class constructor cannot be invoked without 'new'" instead of the
        // framework diagnostic addSystem gives it.
        if(isFunction(s) && !isConstructor(s) && !isClassSource(s)) {
            s = (s as Function)();
        }
        // Both a class and an already-constructed instance go through addSystem;
        // anything else fails there with the "does not extend ClientSystem(...)
        // / ServerSystem(...)" diagnostic.
        const systemInstance = this.addSystem(s as ISystem);
        if(systemInstance) {
          startedSystems.push(systemInstance);
        }
      }
    } finally {
      this._addingSystemOwner = previousOwner;
    }
    if(this.started) {
      startedSystems.forEach(s => {
        s?.onStart?.();
      });
    }
    return startedSystems;
  }

  public getSystemName(system: ISystem | System) : string | number {
    if(isConstructor(system)) {
      return this.constructorNamesToSystemNames[system.name];
    } else {
      return system.name;
    }
  }
    
  public removeSystemsArrayFromFile(systems: Array<ISystem | System | string>, owner?: string ) {
    systems.forEach(s => {
      if(typeof s === 'string') {
        this.removeSystem(s, owner);
      } else {
        if(isConstructor(s)) {
          const systemName = this.constructorNamesToSystemNames[s.name];
          return this.removeSystem(systemName, owner);
        } else {
          this.removeSystem(s.name, owner);
        }
      }
    })
  }
    
  private _addSystem(system: System, atIndex?: number) {
    const specifiedIndex = Number.isInteger(atIndex) && atIndex > -1;
    if(specifiedIndex && atIndex > this.systemArray.length) {
      throw new Error(`Can not at system ${system.name} at index ${atIndex} because that index is greater than the system length`)
    }
    if(system.name in this.systemLookup) {
      throw new Error(`system: ${system.name} already added.`);
    }
    this.systemLookup[system.name] = system;
    if(specifiedIndex) {
      this.systemArray.splice(atIndex, 0, system)
    } else {
      this.systemArray.push(system);
    }

    let remakeArrays = false;

    if(system.onUpdate) {
      system.onUpdate = system.onUpdate.bind(system);
      this._updateOwners.set(system.onUpdate, system);
      if(specifiedIndex) {
        remakeArrays = true;
      } else {
        this.systemUpdates.push(system.onUpdate)
      }
    }

    if(system.onLocalSystemMessage) {
      if(specifiedIndex) {
        remakeArrays = true;
      } else {
        this.systemsWithOnLocalMessages.push(system)
      }
    }

    if(remakeArrays) {
      this.remakeSystemHookImplementationArrays();
    }
    system.$api = this.buildApiForSystem(system) as typeof system.$api;
    // Sandbox / AI-facing diagnostic: if the caller passed a class instance
    // whose prototype chain doesn't include the framework System base, the
    // internal wiring below would crash with an opaque "<internal> is not a
    // function" error — which the sandbox AI fix loop can't act on. Surface
    // a precise, no-internal-names message instead. Common cause: the class
    // didn't call ClientSystem()/ServerSystem() as a function when extending.
    if (typeof (system as any).setProcess !== 'function') {
      throw new Error(
        `System "${String(system?.name ?? 'unknown')}" does not extend ClientSystem(...) or ServerSystem(...) properly. ` +
        `The class is missing required framework wiring — make sure the class extends \`ClientSystem(SharedBase)\` ` +
        `or \`ClientSystem()\` (call it as a function, with parentheses, even when there is no shared base).`,
      );
    }
    system.setProcess(this);

    this.messageQueue.addSystem(system);
    system.initializeEntity = this.entityManager.initializeEntity.bind(this.entityManager);
    system.destroyEntity = this.entityManager.destroyEntity.bind(this.entityManager);
    system.dispatchToSystem = this.messageQueue.add.bind(this.messageQueue);
    system.dispatchToAllSystems = this.messageQueue.addAll.bind(this.messageQueue)
    system.instantDispatchToSystem = this.messageQueue.instantDispatch.bind(this.messageQueue);
    system.instantDispatchToAllSystems = this.messageQueue.instantDispatchAll.bind(this.messageQueue)
    
    // Check if this is the debug system and store reference
    if (system.name === DEBUG_SYSTEM_NAME && 'createSystemLogger' in system) {
      this._debugSystem = system as unknown as IDebugSystem;
      // Inject debug loggers into all previously added systems
      for (const existingSystem of this.systemArray) {
        if (existingSystem !== system) {
          existingSystem.$debugLogger = this._debugSystem.createSystemLogger(String(existingSystem.name));
        }
      }
      // Wrap entityManager methods to inject debug loggers into entities
      this._wrapEntityManagerForDebug();
    }
    
    // Inject debug logger for this system
    if (this._debugSystem && system.name !== DEBUG_SYSTEM_NAME) {
      system.$debugLogger = this._debugSystem.createSystemLogger(String(system.name));
    } else if (!this._debugSystem) {
      system.$debugLogger = NOOP_DEBUG_LOGGER;
    }
    
    this.onAddedSystem(system);
    return system;
  }

  private _removeSystem(system: System | string | number, forRestart=false) : { system?: System, index: number, restartData?: any } {
    const sysName : string = typeof system === 'object' ? system.name as unknown as string : system as unknown as string;
    system = this.systemLookup[sysName];
    if(!system) return { index: -1 };

    let restartData: any = {};
    if(forRestart) {
      restartData = system.onBeforeRestart?.();
    }

    this.messageQueue.removeSystem(system.name);
    
    const apiMethodsAdded = this.apiSystemLookup[system.name];
    if(apiMethodsAdded) {
      apiMethodsAdded.forEach(n => {
        this.systemArray.forEach(s => {
          delete s.$api[n];
        });
        delete this.callerApiFactories[n];
        delete this.$api[n];
      });
      delete this.apiSystemLookup[system.name];
    }

    delete this.systemLookup[sysName];
    const index = this.systemArray.indexOf(system);
    if(index > -1) {
      this.systemArray.splice(index, 1);
    }
    if(system.onUpdate) {
      removeFromArray(this.systemUpdates, system.onUpdate);
    }
    if(system.onLocalSystemMessage) {
      removeFromArray(this.systemsWithOnLocalMessages, system);
    }
    return { system, index, restartData };
  }

  public removeSystem(system: System | string | number, owner?: string) {
    const sysName : string | number = typeof system === 'object' ? system.name as unknown as string | number : system as unknown as string | number;
    if((sysName || sysName === 0) && sysName in this.systemStartCounts) {
      // The file that added this system no longer owns it — other owners (if
      // any) keep it alive and keep it an ambiguous dispatch target.
      if(owner !== undefined) this.systemOwners[sysName]?.delete(owner);
      if(--this.systemStartCounts[sysName] <= 0) {
        delete this.systemStartCounts[sysName];
        delete this.systemOwners[sysName];
        const { system: oldSystem, index } = this._removeSystem(system);
        // Remove any constructor->systemName entries that pointed to this system's name.
        // We cannot rely on oldSystem.constructor.name because decoration may have changed
        // the runtime constructor (e.g., ClientSystem/ServerSystem vs original).
        if (oldSystem) {
          const removedName = oldSystem.name as unknown as string | number;
          for (const ctorName in this.constructorNamesToSystemNames) {
            if (this.constructorNamesToSystemNames[ctorName] === removedName) {
              delete this.constructorNamesToSystemNames[ctorName];
            }
          }
        }
        try {
          oldSystem?.onStop?.();
        } finally {
          if (oldSystem) {
            this.releaseSystem(oldSystem);
            this._notify('onSystemRemoved', oldSystem);
          }
        }
      }
    }
  }

  /**
   * Watch this process: tick and per-system update timings, and systems being
   * added, removed and hot-restarted. Any number of observers; each call
   * returns its own disposer. An observer that throws is detached and
   * reported — it never breaks the tick it observed.
   */
  public observe(observer: ProcessObserver): () => void {
    this._observers = [...this._observers, observer];
    this._refreshObserverLists();
    return () => {
      this._observers = this._observers.filter(o => o !== observer);
      this._refreshObserverLists();
    };
  }

  private _refreshObserverLists() {
    this._tickObservers = this._observers.filter(o => typeof o.onTick === 'function');
    this._systemUpdateObservers = this._observers.filter(o => typeof o.onSystemUpdate === 'function');
  }

  private _notify<K extends keyof ProcessObserver>(hook: K, ...args: Parameters<NonNullable<ProcessObserver[K]>>) {
    if (this._observers.length === 0) return;
    for (const o of this._observers) {
      const fn = o[hook] as ((...a: any[]) => void) | undefined;
      if (typeof fn !== 'function') continue;
      try {
        fn.apply(o, args);
      } catch (err) {
        this._observers = this._observers.filter(x => x !== o);
        this._refreshObserverLists();
        console.error(`[gotti] a process observer threw in ${String(hook)} and was detached`, err);
      }
    }
  }

  public onAfterUpdate(cb: (delta: number) => void) {
    this.afterUpdateHandler = cb;
  }
  public onBeforeUpdate(cb: (delta: number) => void) {
    this.beforeUpdateHandler = cb;
  }
  public offAfterUpdate() {
    delete this.afterUpdateHandler;
  }
  public offBeforeUpdate() {
    delete this.beforeUpdateHandler;
  }
  // Schedule a callback to run on the next tick's before/after phase (always next frame)
  public nextTickBeforeUpdate(cb: (delta: number) => void) { this._beforeNextTickCbs.push(cb); }
  public nextTickAfterUpdate(cb: (delta: number) => void) { this._afterNextTickCbs.push(cb); }
  public update(delta) {
    if (this._tickObservers.length === 0) {
      this._update(delta);
      return;
    }
    const t0 = performance.now();
    try {
      this._update(delta);
    } finally {
      this._notify('onTick', { delta, durationMs: performance.now() - t0, at: Date.now() });
    }
  }

  private _update(delta: number) {
    // Promote next-tick callbacks into current-tick queues by reference,
    // then reset the next-tick arrays for the upcoming frame.
    this._beforeTickCbs = this._beforeNextTickCbs;
    this._beforeNextTickCbs = [];
    this._afterTickCbs = this._afterNextTickCbs;
    this._afterNextTickCbs = [];
    // Before phase for current frame
    for (let i = 0; i < this._beforeTickCbs.length; i++) {
      try { this._beforeTickCbs[i](delta); } catch (err) {
        // A tick callback is a test seam (meters, samplers); a throw must be seen, and must not end the tick.
        console.error('[gotti] a nextTickBeforeUpdate callback threw — the tick continues', err);
      }
    }
    this.beforeUpdateHandler?.(delta);
    if (this._systemUpdateObservers.length === 0) {
      for(let i = 0; i < this.systemUpdates.length; i++) {
        this.systemUpdates[i](delta);
      }
    } else {
      for(let i = 0; i < this.systemUpdates.length; i++) {
        const fn = this.systemUpdates[i];
        const t0 = performance.now();
        try {
          fn(delta);
        } finally {
          const owner = this._updateOwners.get(fn);
          this._notify('onSystemUpdate', { system: owner ? owner.name : '(unknown)', delta, durationMs: performance.now() - t0, at: Date.now() });
        }
      }
    }
    // After phase for current frame
    for (let i = 0; i < this._afterTickCbs.length; i++) {
      try { this._afterTickCbs[i](delta); } catch (err) {
        console.error('[gotti] a nextTickAfterUpdate callback threw — the tick continues', err);
      }
    }
    this.afterUpdateHandler?.(delta);
    for(let i = 0; i < this.systemsWithOnLocalMessages.length; i++) {
      this.messageQueue.dispatch(this.systemsWithOnLocalMessages[i].name);
    }
  }
  public startLoop(...opts: any[]) {
    if(this.startedLoop) throw new Error(`Already started loop.`);
    this.startedLoop = true;
    this.systemArray.forEach(s => {
        s.onStart?.();
    })
    this.onStartLoop(...opts);
  }
  /**
   * Stops the loop: every system hears `onStop`, then the loop ends. A throwing `onStop` keeps neither the other
   * systems from hearing it nor the loop from ending — a loop left ticking after its process was stopped corrupts
   * whatever runs next on the same channels. What the systems threw is raised once the loop has ended: one error as
   * it is, several as an `AggregateError`.
   */
  public stopLoop(...opts: any[]) {
    this.startedLoop = false;
    const errors: unknown[] = [];
    this.systemArray.forEach(s => {
      try {
        s.onStop?.(opts);
      } catch (err) {
        errors.push(err);
      }
    })
    this.onStopLoop(...opts);
    if (errors.length === 1) throw errors[0];
    if (errors.length > 1) throw new AggregateError(errors, `[gotti] ${errors.length} systems threw in onStop; the loop is stopped.`);
  }

  public clear(...opts: any[]) {
    this.messageQueue.clear();
    if(this.startedLoop) {
        this.stopLoop(opts);
    }
    this.systemArray.forEach(s => {
        s.onClear?.(opts);
    });
    this.systemArray.length = 0;
    this.systemUpdates.length = 0;
    this.systemsWithOnLocalMessages.length = 0;
  // Clear any scheduled next-tick callbacks
  this._beforeTickCbs.length = 0;
  this._afterTickCbs.length = 0;
  this._beforeNextTickCbs.length = 0;
  this._afterNextTickCbs.length = 0;
    clearObj(this.systemStartCounts);
    clearObj(this.constructorNamesToSystemNames);
    clearObj(this.systemLookup);
  }
}
